import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
	GlobalDataManagementEntry,
	GlobalDataManagementObjectTypes,
	GlobalDataManagementStatuses,
} from '../../db-models/global-data-management';
import * as actions from './global-data-management.actions';
import * as requestActions from './global-data-management.request.actions';
import firebase from 'firebase/compat/app';
import 'firebase/firestore';
import { createId } from '@core/util/file';
import { cloneDeep } from 'lodash';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { AppState } from '@state/app/app.state';
import { UserProfileState } from '@state/user-profile';
import { FirestoreStateBase } from '@state/FirestoreStateBase';
import { GlobalStateModel } from '@state/FirestoreStateBaseForGlobal';
import { CollectionPaths } from '@models/collections';
import { Logger } from '@core/logger';
import { BusinessState } from '@state/business';

interface GlobalDataManagementModel extends GlobalStateModel {
	entries: GlobalDataManagementEntry[];
}

@State<GlobalDataManagementModel>({
	name: 'globalDataManagement',
	defaults: {
		entries: [],
	},
})
@Injectable()
export class GlobalDataManagementState extends FirestoreStateBase<
	GlobalDataManagementEntry,
	string,
	GlobalDataManagementModel
> {
	@SelectSnapshot(BusinessState.isCollege) isCollege;

	constructor() {
		super(
			() => CollectionPaths.tmp_global_data_management(),
			(payload) => ({ entries: payload as GlobalDataManagementEntry[] }),
			() => ({ entries: [] }),
		);
	}

	@Action(requestActions.RequestFinancialInstitutionChange)
	requestFinancialInstitutionChange(
		ctx: StateContext<GlobalDataManagementModel>,
		{ institution }: requestActions.RequestFinancialInstitutionChange,
	) {
		if (institution?.id) {
			const tmpFinancialInstitution = cloneDeep(institution);
			delete tmpFinancialInstitution?.branch;
			delete tmpFinancialInstitution?.investors;

			ctx.dispatch(
				new actions.Create({
					globalId: tmpFinancialInstitution.id,
					parentId: null,
					object: tmpFinancialInstitution,
					objectType: GlobalDataManagementObjectTypes.FINANCIAL_INSTITUTION,
				}),
			);
		}
	}

	@Action(requestActions.RequestBranchChange)
	requestFinancialInstitutionBranchChange(
		ctx: StateContext<GlobalDataManagementModel>,
		{ payload }: requestActions.RequestBranchChange,
	) {
		if (payload.institutionId && payload.branch?.id) {
			ctx.dispatch(
				new actions.Create({
					globalId: payload.branch.id,
					parentId: payload.institutionId,
					object: payload.branch,
					objectType:
						GlobalDataManagementObjectTypes.FINANCUAL_INSTITUTION_BRANCHES,
				}),
			);
		}
	}

	@Action(requestActions.RequestLawFirmChange)
	requestLawFirmChange(
		ctx: StateContext<GlobalDataManagementModel>,
		{ lawfirm }: requestActions.RequestLawFirmChange,
	) {
		const tmpLawfirm = cloneDeep(lawfirm);
		delete tmpLawfirm?.lawyer;
		delete tmpLawfirm?.clerk;
		if (lawfirm.id) {
			ctx.dispatch(
				new actions.Create({
					globalId: tmpLawfirm.id,
					parentId: null,
					object: tmpLawfirm,
					objectType: GlobalDataManagementObjectTypes.LAWFIRM,
				}),
			);
		}
	}

	@Action(requestActions.RequestLawFirmEmployeeChange)
	requestLawFirmEmployeeChange(
		ctx: StateContext<GlobalDataManagementModel>,
		{ payload }: requestActions.RequestLawFirmEmployeeChange,
	) {
		if (payload.lawfirmId && payload.lawfirmEmployee?.id) {
			ctx.dispatch(
				new actions.Create({
					globalId: payload.lawfirmEmployee.id,
					parentId: payload.lawfirmId,
					object: payload.lawfirmEmployee,
					objectType: GlobalDataManagementObjectTypes.LAWFIRM_EMPLOYEE,
				}),
			);
		}
	}

	@Action(actions.Create)
	async create(
		ctx: StateContext<GlobalDataManagementModel>,
		{ payload }: actions.Create,
	): Promise<void> {
		// Do not allow colleges to pollute the global data management
		if (this.isCollege) return;

		const userProfile = this.store.selectSnapshot(UserProfileState.getProfile);
		const globalDataManagementEntry: GlobalDataManagementEntry = {
			globalId: payload.globalId,
			parentId: payload.parentId,
			object: payload.object,
			requestedByCompanyId: userProfile.currentBusinessId,
			requestedByUserId: userProfile.id,
			requestedByEmail: userProfile.email,
			status: GlobalDataManagementStatuses.PENDING,
			createdAt: firebase.firestore.FieldValue.serverTimestamp(),
			objectType: payload.objectType,
			id: createId(),
		};

		try {
			await this.repo.Add(globalDataManagementEntry);
		} catch (e) {
			// no need to for the user to know about this, this just adds data to the global data management, but they have
			// within their business
			Logger.exception(e);
		}
	}
}
